import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { FileText, Bot, MessageSquare, Database, TrendingUp, GitBranch, CheckCircle2, Copy, FileEdit, ChevronDown } from 'lucide-react';

interface TransformationLogEntry {
  step: string;
  method?: string;
  template?: string;
  from?: string;
  to?: string;
  of_question_text?: string;
  similarity_score?: number;
  merged_from?: string[];
  duplicate_count?: number;
  timestamp: string;
}

interface SourceEvidence {
  template?: string;
  term?: string;
  type?: string;
  parent_question?: string;
  original_term?: string;
  also_found_in_paa?: boolean;
}

interface QuestionProvenanceProps {
  question: {
    text: string;
    original_text?: string;
    source: string;
    sources?: string[];
    source_evidence?: SourceEvidence;
    transformation_log?: TransformationLogEntry[];
    status?: string;
    duplicate_of?: string;
    similarity_score?: string;
    grammar_corrected?: boolean;
    merged_from_questions?: string[];
  };
}

export function QuestionProvenance({ question }: QuestionProvenanceProps) {
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'template':
        return <FileText className="w-4 h-4" />;
      case 'ai':
        return <Bot className="w-4 h-4" />;
      case 'google_paa':
        return <Database className="w-4 h-4" />;
      case 'reviews':
        return <MessageSquare className="w-4 h-4" />;
      case 'google_ads':
        return <TrendingUp className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getSourceLabel = (source: string) => {
    const labels: Record<string, string> = {
      template: 'Template',
      ai: 'AI Generated',
      google_paa: 'Google PAA',
      reviews: 'Customer Reviews',
      google_ads: 'Google Ads'
    };
    return labels[source] || source;
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'kept':
      case 'kept_multi_source':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'duplicate':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'generated':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status?: string) => {
    const labels: Record<string, string> = {
      kept: 'Kept',
      kept_multi_source: 'Kept (Multi-Source)',
      duplicate: 'Duplicate',
      generated: 'Generated'
    };
    return labels[status || 'generated'] || status;
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const hasProvenance = question.original_text || question.transformation_log?.length || question.source_evidence;

  if (!hasProvenance) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Provenance Information</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No provenance information available for this question.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <GitBranch className="w-4 h-4" />
          Question Provenance
        </CardTitle>
        <CardDescription>Full lineage and transformation history</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Badge */}
        {question.status && (
          <div>
            <Label className="text-xs font-medium mb-1 block">Status</Label>
            <Badge className={getStatusColor(question.status)}>
              {getStatusLabel(question.status)}
            </Badge>
            {question.grammar_corrected && (
              <Badge variant="secondary" className="ml-2">
                <FileEdit className="w-3 h-3 mr-1" />
                Grammar Corrected
              </Badge>
            )}
          </div>
        )}

        {/* Source Information */}
        <div>
          <Label className="text-xs font-medium mb-2 block">Generation Source{question.sources && question.sources.length > 1 ? 's' : ''}</Label>
          <div className="flex flex-wrap gap-2">
            {question.sources && question.sources.length > 1 ? (
              question.sources.map((source, idx) => (
                <Badge key={idx} variant="outline" className="flex items-center gap-1">
                  {getSourceIcon(source)}
                  {getSourceLabel(source)}
                </Badge>
              ))
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                {getSourceIcon(question.source)}
                {getSourceLabel(question.source)}
              </Badge>
            )}
          </div>
        </div>

        {/* Original Text (if different) */}
        {question.original_text && question.original_text !== question.text && (
          <div>
            <Label className="text-xs font-medium mb-1 block">Original Text</Label>
            <div className="text-sm text-muted-foreground p-2 bg-muted rounded border">
              "{question.original_text}"
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              → Transformed to: "{question.text}"
            </p>
          </div>
        )}

        {/* Duplicate Information */}
        {question.status === 'duplicate' && question.similarity_score && (
          <div>
            <Label className="text-xs font-medium mb-1 block">Duplicate Information</Label>
            <div className="text-sm p-2 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                <span>Marked as duplicate (similarity: {Math.round(parseFloat(question.similarity_score) * 100)}%)</span>
              </div>
            </div>
          </div>
        )}

        {/* Merged Sources */}
        {question.merged_from_questions && question.merged_from_questions.length > 0 && (
          <div>
            <Label className="text-xs font-medium mb-1 block">Merged Sources</Label>
            <div className="text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>Merged from {question.merged_from_questions.length} duplicate question{question.merged_from_questions.length > 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        )}

        {/* Source Evidence */}
        {question.source_evidence && Object.keys(question.source_evidence).length > 0 && (
          <div>
            <Label className="text-xs font-medium mb-2 block">Source Evidence</Label>
            <div className="text-xs space-y-1 p-2 bg-muted rounded border">
              {question.source_evidence.template && (
                <div>
                  <span className="font-medium">Template:</span> {question.source_evidence.template}
                </div>
              )}
              {question.source_evidence.term && (
                <div>
                  <span className="font-medium">Term:</span> {question.source_evidence.term}
                </div>
              )}
              {question.source_evidence.parent_question && (
                <div>
                  <span className="font-medium">Parent Question:</span> {question.source_evidence.parent_question}
                </div>
              )}
              {question.source_evidence.original_term && (
                <div>
                  <span className="font-medium">Google Ads Term:</span> {question.source_evidence.original_term}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transformation Timeline */}
        {question.transformation_log && question.transformation_log.length > 0 && (
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer text-xs font-medium p-2 hover:bg-muted rounded">
              <span>Transformation History ({question.transformation_log.length} step{question.transformation_log.length > 1 ? 's' : ''})</span>
              <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
            </summary>
            <div className="space-y-3 pt-4">
              {question.transformation_log.map((entry, idx) => (
                <div key={idx} className="relative pl-4 pb-3 border-l-2 border-muted last:border-l-0 last:pb-0">
                  <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-primary border-2 border-background" />
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium capitalize">{entry.step.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </div>

                    {entry.method && (
                      <div className="text-xs text-muted-foreground">
                        Method: {entry.method}
                      </div>
                    )}

                    {entry.template && (
                      <div className="text-xs text-muted-foreground">
                        Template: {entry.template}
                      </div>
                    )}

                    {entry.from && entry.to && (
                      <div className="text-xs space-y-1 mt-2">
                        <div className="text-red-600 dark:text-red-400">- "{entry.from}"</div>
                        <div className="text-green-600 dark:text-green-400">+ "{entry.to}"</div>
                      </div>
                    )}

                    {entry.similarity_score !== undefined && (
                      <div className="text-xs text-muted-foreground">
                        Similarity: {Math.round(entry.similarity_score * 100)}%
                      </div>
                    )}

                    {entry.duplicate_count !== undefined && (
                      <div className="text-xs text-muted-foreground">
                        Merged {entry.duplicate_count} duplicate{entry.duplicate_count > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
